/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import moment from 'moment';

import { Container, FlexLayout, FlexItem, VSpacer } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';
import { getTid } from 'utils/tid';
import { FILTER_RANGE_TYPE, FILTER_RANGE_SUBTYPE } from './filterProps';

import DateTimeRangeTypeSelector from './DateTimeRangeTypeSelector';
import DateTimePane from './DateTimePane';

import './DateTimeFilter.scss';

/*
 * Input dates are assumed to be in UTC. The timezone property is propagated to
 * the pickers, but is expected to be in UTC (as full timezone support is not
 * currently required).
 */
export default class DateTimeFilter extends Component<any, any> {
	static propTypes = {
		fromValue: PropTypes.string,
		toValue: PropTypes.string,
		rangeType: PropTypes.string,
		rangeSubtype: PropTypes.string,
		dataType: PropTypes.string,
		inverted: PropTypes.bool,
		includeNulls: PropTypes.bool,
		timezone: PropTypes.string.isRequired,
		onChange: PropTypes.func.isRequired,
		locale: PropTypes.string.isRequired,
		readOnly: PropTypes.bool,
		isDirty: PropTypes.bool,
		isValid: PropTypes.bool,
		validRangeTypeOptions: PropTypes.array,
	};

	static defaultProps = {
		fromValue: '',
		toValue: '',
		rangeType: FILTER_RANGE_TYPE.BETWEEN,
		rangeSubtype: FILTER_RANGE_SUBTYPE.DATE_AND_TIME,
		dataType: 'datetime',
		readOnly: false,
	};

	constructor(props) {
		super(props);
		this.state = this._getStateFromProps(props);
	}

	componentWillReceiveProps(nextProps) {
		this.setState(this._getStateFromProps(nextProps));
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (this.props.rangeType !== nextProps.rangeType) {
			return true;
		}
		if (this.props.rangeSubtype !== nextProps.rangeSubtype) {
			return true;
		}
		if (this.props.readOnly !== nextProps.readOnly) {
			return true;
		}

		switch (nextProps.dataType) {
			case 'datetime': {
				return (
					this._shouldComponentUpdateDateCheck(nextProps, nextState) ||
					this._shouldComponentUpdateTimeCheck(nextProps, nextState)
				);
			}
			case 'date':
				return this._shouldComponentUpdateDateCheck(nextProps, nextState);
			case 'time':
				return this._shouldComponentUpdateTimeCheck(nextProps, nextState);
			default:
		}

		return false;
	}

	static ISO_DATE_FORMAT = 'YYYY-MM-DD';
	static ISO_TIME_FORMAT = 'HH:mm:ss';
	static ISO_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
	static DISPLAY_TIME_FORMAT = 'hh:mm:ss a';

	_fromDateTimePaneNode: DateTimePane;
	_toDateTimePaneNode: DateTimePane;

	_shouldComponentUpdateDateCheck(nextProps, nextState) {
		const fromDatePickerValue =
			this._fromDateTimePaneNode && this._fromDateTimePaneNode.getDate();
		const toDatePickerValue = this._toDateTimePaneNode && this._toDateTimePaneNode.getDate();

		switch (nextProps.rangeType) {
			case FILTER_RANGE_TYPE.BETWEEN:
			case FILTER_RANGE_TYPE.NOT_BETWEEN:
				if (
					this._hasDateChanged(fromDatePickerValue, nextState.fromDate) ||
					this._hasDateChanged(toDatePickerValue, nextState.toDate)
				) {
					return true;
				}
				break;
			case FILTER_RANGE_TYPE.AFTER:
				if (this._hasDateChanged(fromDatePickerValue, nextState.fromDate)) {
					return true;
				}
				break;
			case FILTER_RANGE_TYPE.BEFORE:
				if (this._hasDateChanged(toDatePickerValue, nextState.toDate)) {
					return true;
				}
				break;
			default:
		}

		return false;
	}

	_shouldComponentUpdateTimeCheck(nextProps, nextState) {
		const fromTimePickerValue =
			this._fromDateTimePaneNode && this._fromDateTimePaneNode.getTime();
		const toTimePickerValue = this._toDateTimePaneNode && this._toDateTimePaneNode.getTime();

		switch (nextProps.rangeType) {
			case FILTER_RANGE_TYPE.BETWEEN:
			case FILTER_RANGE_TYPE.NOT_BETWEEN:
				if (
					this._hasTimeChanged(fromTimePickerValue, nextState.fromTime) ||
					this._hasTimeChanged(toTimePickerValue, nextState.toTime)
				) {
					return true;
				}
				break;
			case FILTER_RANGE_TYPE.AFTER:
				if (this._hasTimeChanged(fromTimePickerValue, nextState.fromTime)) {
					return true;
				}
				break;
			case FILTER_RANGE_TYPE.BEFORE:
				if (this._hasTimeChanged(toTimePickerValue, nextState.toTime)) {
					return true;
				}
				break;
			default:
		}

		return false;
	}

	_getStateFromProps(nextProps) {
		const { dataType, fromValue, toValue } = nextProps;

		switch (dataType) {
			case 'datetime': {
				const fromDate = fromValue ? this._getUTCDatePart(fromValue) : fromValue;
				const fromTime = fromValue ? this._getUTCTimePart(fromValue) : fromValue;
				const fromTimeDisplay = fromValue
					? this._getUTCTimePart(fromValue, DateTimeFilter.DISPLAY_TIME_FORMAT)
					: fromValue;
				const toDate = toValue ? this._getUTCDatePart(toValue) : toValue;
				const toTime = toValue ? this._getUTCTimePart(toValue) : toValue;
				const toTimeDisplay = toValue
					? this._getUTCTimePart(toValue, DateTimeFilter.DISPLAY_TIME_FORMAT)
					: toValue;

				return this.transferExistingState({
					fromDate,
					fromTime,
					fromTimeDisplay,
					toDate,
					toTime,
					toTimeDisplay,
				});
			}
			case 'date': {
				const fromDate = fromValue ? this._getUTCDatePart(fromValue) : fromValue;
				const toDate = toValue ? this._getUTCDatePart(toValue) : toValue;

				return { fromDate, toDate };
			}
			case 'time': {
				const fromTime = fromValue ? this._getUTCTimePart(fromValue) : fromValue;
				const fromTimeDisplay = fromValue
					? this._getUTCTimePart(fromValue, DateTimeFilter.DISPLAY_TIME_FORMAT)
					: fromValue;
				const toTime = toValue ? this._getUTCTimePart(toValue) : toValue;
				const toTimeDisplay = toValue
					? this._getUTCTimePart(toValue, DateTimeFilter.DISPLAY_TIME_FORMAT)
					: toValue;

				return { fromTime, fromTimeDisplay, toTime, toTimeDisplay };
			}
			default:
				return null;
		}
	}

	transferExistingState(state) {
		if (this.state) {
			if (this.state.fromDate && !state.fromDate) state.fromDate = this.state.fromDate;
			if (this.state.toDate && !state.toDate) state.toDate = this.state.toDate;
			if (this.state.fromTime && !state.fromTime) state.fromTime = this.state.fromTime;
			if (this.state.toTime && !state.toTime) state.toTime = this.state.toTime;
			if (this.state.fromTimeDisplay && !state.fromTimeDisplay) {
				state.fromTimeDisplay = this.state.fromTimeDisplay;
			}
			if (this.state.toTimeDisplay && !state.toTimeDisplay) {
				state.toTimeDisplay = this.state.toTimeDisplay;
			}
		}

		return state;
	}

	_hasTimeChanged = (inputTime, changedTime) => {
		if (inputTime && changedTime) {
			return inputTime !== changedTime;
		} else if (inputTime || changedTime) {
			return true;
		}

		return false;
	};

	_hasDateChanged = (inputDate, changedDate) => {
		if (inputDate && changedDate) {
			return inputDate !== changedDate;
		} else if (inputDate || changedDate) {
			return true;
		}

		return false;
	};

	_onRangeTypeChange = changed => {
		const result = {
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
			rangeType: changed,
			rangeSubtype: this.props.rangeSubtype,
			includeNulls: this.props.includeNulls,
			inverted: changed === FILTER_RANGE_TYPE.NOT_BETWEEN,
		};

		this.props.onChange(result);
	};

	_onRangeSubtypeChange = changed => {
		const result = {
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
			rangeType: this.props.rangeType,
			rangeSubtype: changed,
			includeNulls: this.props.includeNulls,
			inverted: changed === FILTER_RANGE_TYPE.NOT_BETWEEN,
		};

		this.props.onChange(result);
	};

	_onFromValueDateChange = fromDate => {
		if (!fromDate || this.props.readOnly) {
			this._revertFromDate();

			return;
		}
		const { toValue, rangeType, rangeSubtype, includeNulls, inverted, dataType } = this.props;
		const { fromTime } = this.state;

		switch (dataType) {
			case 'datetime':
				if (fromTime) {
					const datetime = moment.utc(`${fromDate} ${fromTime}`);

					const validFromValue = datetime.format(DateTimeFilter.ISO_DATETIME_FORMAT);

					this.props.onChange({
						fromValue: validFromValue,
						toValue,
						rangeType,
						rangeSubtype,
						includeNulls,
						inverted,
						format: DateTimeFilter.ISO_DATETIME_FORMAT,
					});
				} else if (
					this._isValidFromDate(moment.utc(fromDate, DateTimeFilter.ISO_DATE_FORMAT))
				) {
					const state = this._getStateFromProps(this.props);

					state.fromDate = fromDate;
					this.setState(state);
				} else {
					this._revertFromDate();
				}
				break;
			case 'date': {
				this.props.onChange({
					fromValue: fromDate,
					toValue,
					rangeType,
					rangeSubtype,
					includeNulls,
					inverted,
					format: DateTimeFilter.ISO_DATE_FORMAT,
				});
				break;
			}
			default:
		}
	};

	_onFromValueTimeChange = fromTime => {
		if (!fromTime || this.props.readOnly) {
			this._revertFromTime();

			return;
		}
		const { toValue, rangeType, rangeSubtype, includeNulls, inverted, dataType } = this.props;
		const { fromDate } = this.state;

		switch (dataType) {
			case 'datetime':
				if (fromDate) {
					if (this._hasTimeChanged(this.state.fromTime, fromTime)) {
						const datetime = moment.utc(`${fromDate} ${fromTime}`);

						const validFromValue = datetime.format(DateTimeFilter.ISO_DATETIME_FORMAT);

						this.props.onChange({
							fromValue: validFromValue,
							toValue,
							rangeType,
							rangeSubtype,
							includeNulls,
							inverted,
							format: DateTimeFilter.ISO_DATETIME_FORMAT,
						});
					}
				} else {
					const state = this._getStateFromProps(this.props);

					state.fromTime = fromTime;
					this.setState(state);
				}
				break;
			case 'time': {
				if (this._hasTimeChanged(this.props.fromValue, fromTime)) {
					this.props.onChange({
						fromValue: fromTime,
						toValue,
						rangeType,
						rangeSubtype,
						includeNulls,
						inverted,
						format: DateTimeFilter.ISO_TIME_FORMAT,
					});
				}
				break;
			}
			default:
		}
	};

	_onToValueDateChange = toDate => {
		if (!toDate || this.props.readOnly) {
			this._revertToDate();

			return;
		}
		const { fromValue, rangeType, rangeSubtype, includeNulls, inverted, dataType } = this.props;
		const { toTime } = this.state;

		switch (dataType) {
			case 'datetime':
				if (toTime) {
					const datetime = moment.utc(`${toDate} ${toTime}`);

					const validToValue = datetime.format(DateTimeFilter.ISO_DATETIME_FORMAT);

					this.props.onChange({
						fromValue,
						toValue: validToValue,
						rangeType,
						rangeSubtype,
						includeNulls,
						inverted,
						format: DateTimeFilter.ISO_DATETIME_FORMAT,
					});
				} else if (
					this._isValidToDate(moment.utc(toDate, DateTimeFilter.ISO_DATE_FORMAT))
				) {
					const state = this._getStateFromProps(this.props);

					state.toDate = toDate;
					this.setState(state);
				} else {
					this._revertToDate();
				}
				break;
			case 'date': {
				this.props.onChange({
					fromValue,
					toValue: toDate,
					rangeType,
					rangeSubtype,
					includeNulls,
					inverted,
					format: DateTimeFilter.ISO_DATE_FORMAT,
				});

				break;
			}
			default:
		}
	};

	_onToValueTimeChange = toTime => {
		if (!toTime || this.props.readOnly) {
			this._revertToTime();

			return;
		}
		const { fromValue, rangeType, rangeSubtype, includeNulls, inverted, dataType } = this.props;
		const { toDate } = this.state;

		switch (dataType) {
			case 'datetime':
				if (toDate) {
					if (this._hasTimeChanged(this.state.toTime, toTime)) {
						const datetime = moment.utc(`${toDate} ${toTime}`);

						const validToValue = datetime.format(DateTimeFilter.ISO_DATETIME_FORMAT);

						this.props.onChange({
							fromValue,
							toValue: validToValue,
							rangeType,
							rangeSubtype,
							includeNulls,
							inverted,
							format: DateTimeFilter.ISO_DATETIME_FORMAT,
						});
					}
				} else {
					const state = this._getStateFromProps(this.props);

					state.toTime = toTime;
					this.setState(state);
				}
				break;
			case 'time': {
				if (this._hasTimeChanged(this.props.toValue, toTime)) {
					this.props.onChange({
						fromValue,
						toValue: toTime,
						rangeType,
						rangeSubtype,
						includeNulls,
						inverted,
						format: DateTimeFilter.ISO_TIME_FORMAT,
					});
				}
				break;
			}
			default:
		}
	};

	_isValidFromDate = fromDate => {
		const { toDate } = this.state;

		if (toDate && fromDate.isAfter(moment.utc(toDate))) {
			return false;
		}

		return true;
	};

	_isValidToDate = toDate => {
		const { fromDate } = this.state;

		if (fromDate && toDate.isBefore(moment.utc(fromDate))) {
			return false;
		}

		return true;
	};

	_getUTCDatePart = (utcDateTimeString: string, format?) => {
		const { dataType } = this.props;
		const dateFormat = format || DateTimeFilter.ISO_DATE_FORMAT;

		switch (dataType) {
			case 'date':
				return moment.utc(utcDateTimeString, dateFormat).format(dateFormat);
			case 'datetime':
				return moment.utc(utcDateTimeString).format(dateFormat);
			default:
				return null;
		}
	};

	_getUTCTimePart = (utcDateTimeString: string, format?) => {
		const { dataType } = this.props;
		const timeFormat = format || DateTimeFilter.ISO_TIME_FORMAT;

		switch (dataType) {
			case 'time':
				return moment.utc(utcDateTimeString, timeFormat).format(timeFormat);
			case 'datetime':
				return moment.utc(utcDateTimeString).format(timeFormat);
			default:
				return null;
		}
	};

	_refFromDateTimePane = node => {
		this._fromDateTimePaneNode = node;
	};

	_refToDateTimePane = node => {
		this._toDateTimePaneNode = node;
	};

	_revertFromDate = () => {
		const { fromDate } = this.state;
		/* eslint-disable no-underscore-dangle */

		this._fromDateTimePaneNode._revertDate(fromDate);
		/* eslint-enable no-underscore-dangle */
	};

	_revertFromTime = () => {
		const { fromTime, toTime, fromTimeDisplay, toTimeDisplay } = this.state;
		/* eslint-disable no-underscore-dangle */
		const fromTimePickerValue = this._fromDateTimePaneNode.getTime();

		if (!fromTime && toTime && this._hasTimeChanged(fromTimePickerValue, toTime)) {
			this._fromDateTimePaneNode._revertTime(toTimeDisplay);
		} else if (this._hasTimeChanged(fromTimePickerValue, fromTime)) {
			this._fromDateTimePaneNode._revertTime(fromTimeDisplay);
		}
		/* eslint-enable no-underscore-dangle */
	};

	_revertToDate = () => {
		const { toDate } = this.state;
		/* eslint-disable no-underscore-dangle */

		this._toDateTimePaneNode._revertDate(toDate);
		/* eslint-enable no-underscore-dangle */
	};

	_revertToTime = () => {
		const { fromTime, toTime, fromTimeDisplay, toTimeDisplay } = this.state;
		/* eslint-disable no-underscore-dangle */
		const toTimePickerValue = this._toDateTimePaneNode.getTime();

		if (!toTime && fromTime && this._hasTimeChanged(toTimePickerValue, fromTime)) {
			this._toDateTimePaneNode._revertTime(fromTimeDisplay);
		} else if (this._hasTimeChanged(toTimePickerValue, toTime)) {
			this._toDateTimePaneNode._revertTime(toTimeDisplay);
		}
		/* eslint-enable no-underscore-dangle */
	};

	_renderRangeTypeSelector = () => {
		// specify 'subtype' when dealing with 'datetime' only
		const subtype = this.props.dataType === 'datetime' ? this.props.rangeSubtype : undefined;

		return (
			<DateTimeRangeTypeSelector
				onChangeRangeType={this._onRangeTypeChange}
				onChangeRangeSubtype={this._onRangeSubtypeChange}
				locale={this.props.locale}
				rangeType={this.props.rangeType}
				rangeSubtype={subtype}
				readOnly={this.props.readOnly}
				validRangeTypeOptions={this.props.validRangeTypeOptions}
			/>
		);
	};

	_isDateOnly = () => this.props.rangeSubtype === FILTER_RANGE_SUBTYPE.DATE_ONLY;

	_renderAfter = () => {
		const { locale, timezone, dataType, readOnly } = this.props;
		const { fromDate, fromTimeDisplay } = this.state;

		return (
			<FlexLayout fullHeight direction="column">
				{this._renderRangeTypeSelector()}
				<VSpacer />
				<FlexItem grow gutter={[0, 2, 0, 2]} className="bi-mui-dateTimeFilter__box">
					<Container gutter={[2, 0, 1, 0]}>{stringResource.get('after')}</Container>
					<DateTimePane
						inputDate={fromDate}
						inputTime={fromTimeDisplay}
						locale={locale}
						timezone={timezone}
						dataType={dataType}
						onDateChange={this._onFromValueDateChange}
						onTimeChange={this._onFromValueTimeChange}
						ref={this._refFromDateTimePane}
						dateOnly={this._isDateOnly()}
						readOnly={readOnly}
					/>
				</FlexItem>
			</FlexLayout>
		);
	};

	_renderBefore = () => {
		const { locale, timezone, dataType, readOnly } = this.props;
		const { toDate, toTimeDisplay } = this.state;

		return (
			<FlexLayout fullHeight direction="column">
				{this._renderRangeTypeSelector()}
				<VSpacer />
				<FlexItem grow gutter={[0, 2, 0, 2]} className="bi-mui-dateTimeFilter__box">
					<Container gutter={[2, 0, 1, 0]}>{stringResource.get('before')}</Container>
					<DateTimePane
						inputDate={toDate}
						inputTime={toTimeDisplay}
						locale={locale}
						timezone={timezone}
						dataType={dataType}
						onDateChange={this._onToValueDateChange}
						onTimeChange={this._onToValueTimeChange}
						ref={this._refToDateTimePane}
						dateOnly={this._isDateOnly()}
						readOnly={readOnly}
					/>
				</FlexItem>
			</FlexLayout>
		);
	};

	_renderBetween = () => {
		const {
			locale,
			timezone,
			dataType,
			fromValue,
			toValue,
			readOnly,
			isDirty,
			isValid,
		} = this.props;
		const { fromDate, fromTimeDisplay, toDate, toTimeDisplay } = this.state;

		return (
			<FlexLayout fullHeight direction="column" data-tid={getTid('dateTimeFilter')}>
				{this._renderRangeTypeSelector()}
				<VSpacer />
				<FlexItem grow gutter={[0, 2, 0, 2]} className="bi-mui-dateTimeFilter__box">
					<Container gutter={[2, 0, 1, 0]}>{stringResource.get('from')}</Container>
					<Container data-tid={getTid('dateTimeFilter', 'pickerSection1')}>
						<DateTimePane
							locale={locale}
							timezone={timezone}
							dataType={dataType}
							inputDate={fromDate}
							inputTime={fromTimeDisplay}
							onDateChange={this._onFromValueDateChange}
							onTimeChange={this._onFromValueTimeChange}
							ref={this._refFromDateTimePane}
							maxDate={toValue}
							ariaLabelDate={stringResource.get('fromDate')}
							ariaLabelTime={stringResource.get('fromTime')}
							dateOnly={this._isDateOnly()}
							readOnly={readOnly}
						/>
					</Container>
					<Container gutter={[3, 0, 1, 0]}>{stringResource.get('to')}</Container>
					<Container data-tid={getTid('dateTimeFilter', 'pickerSection2')}>
						<DateTimePane
							locale={locale}
							timezone={timezone}
							dataType={dataType}
							inputDate={toDate}
							inputTime={toTimeDisplay}
							onDateChange={this._onToValueDateChange}
							onTimeChange={this._onToValueTimeChange}
							ref={this._refToDateTimePane}
							minDate={fromValue}
							ariaLabelDate={stringResource.get('toDate')}
							ariaLabelTime={stringResource.get('toTime')}
							dateOnly={this._isDateOnly()}
							readOnly={readOnly}
						/>
					</Container>
					{isDirty && !isValid && (
						<Container
							gutter={[3, 0, 1, 0]}
							className="bi-mui-dateTimeFilter__validation"
							data-tid={getTid('dateTimeFilter', 'stringDateValidation')}
						>
							{stringResource.get('dateValidation')}
						</Container>
					)}
				</FlexItem>
			</FlexLayout>
		);
	};

	render() {
		switch (this.props.rangeType) {
			case FILTER_RANGE_TYPE.BETWEEN:
			case FILTER_RANGE_TYPE.NOT_BETWEEN:
				return this._renderBetween();
			case FILTER_RANGE_TYPE.AFTER:
				return this._renderAfter();
			case FILTER_RANGE_TYPE.BEFORE:
				return this._renderBefore();
			default:
				return null;
		}
	}
}
